import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, Download, LogOut, FileText, CheckCircle, 
  MapPin, Clock, ShieldCheck, Activity, Inbox, 
  ListTodo, CheckSquare, XCircle, ArrowRight
} from 'lucide-react';
import { Toast } from '../../components/ui/Toast';
import { apiGet, createHeaders, getJWT, logout, API_BASE_URL } from '../../auth';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = API_BASE_URL;

interface SurveyorAssignment {
  id: string;
  report_id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  assigned_at: string;
  report_history: {
    guest_token: string;
    project_description: string;
    cargo_weight_mt: number;
    created_at: string;
  };
  customer_company?: string;
  pdf_url?: string;
}

export const SurveyorDashboard: React.FC = () => {
  const [assignments, setAssignments] = useState<SurveyorAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, language } = useLanguage();
  
  // Tabs mapped to sidebar
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'completed'>('pending');
  
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    if (!getJWT()) return; // Exit early during logout teardown to prevent 403
    try {
      const data = await apiGet<SurveyorAssignment[]>('api/surveyor/assignments');
      setAssignments(data);
    } catch (err: any) {
      if (err.message !== 'Not authenticated') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    setUpdatingId(id);
    try {
      const headers = createHeaders(getJWT());
      headers.set('Content-Type', 'application/json');
      const response = await fetch(`${API_URL}/api/surveyor/assignments/${id}/status`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error(t('surveys.portal.dashboard.update_error'));
      fetchAssignments();
      setToast({ 
        message: newStatus === 'accepted' ? t('surveys.portal.dashboard.accept_success') : t('surveys.portal.dashboard.reject_success'), 
        type: 'success' 
      });
    } catch (err: any) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDownloadPdf = async (assignment: SurveyorAssignment) => {
    try {
      setToast({ message: t('surveys.portal.dashboard.downloading_pdf'), type: 'success' });
      const token = localStorage.getItem('jwt');
      const proxyUrl = API_URL.replace('8000', '8001') + `/api/surveyor/assignments/${assignment.id}/pdf`;
      const res = await fetch(proxyUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to download Proxy PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Surveyor_Report_${assignment.id.substring(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setToast({ message: t('surveys.portal.dashboard.download_success'), type: 'success' });
    } catch (e) {
      setToast({ message: t('surveys.portal.dashboard.download_error'), type: 'error' });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex justify-center items-center font-sans">
      <div className="flex flex-col items-center gap-3">
         <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
         <span className="text-slate-500 font-medium tracking-wide">{t('surveys.portal.dashboard.connecting')}</span>
      </div>
    </div>
  );

  const pendingAssignments = assignments.filter(a => a.status === 'pending');
  const activeAssignments = assignments.filter(a => a.status === 'accepted');
  const completedAssignments = assignments.filter(a => a.status === 'completed' || a.status === 'rejected');

  const getActiveList = () => {
    if (activeTab === 'pending') return pendingAssignments;
    if (activeTab === 'active') return activeAssignments;
    return completedAssignments;
  };

  const getTabTitle = () => {
    if (activeTab === 'pending') return t('surveys.portal.dashboard.pending_tab');
    if (activeTab === 'active') return t('surveys.portal.dashboard.active_tab');
    if (activeTab === 'completed') return t('surveys.portal.dashboard.completed_tab');
    return '';
  };

  return (
    <div className="flex h-screen bg-[#F9FAFB] font-sans text-slate-800 overflow-hidden">
      
      {/* 1. LEFT SIDEBAR */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col justify-between shrink-0 shadow-xl z-20 hidden md:flex">
        <div>
          {/* Logo / Brand Area */}
          <div className="h-16 flex items-center px-6 border-b border-white/10 bg-black/20">
             <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3">
               <ShieldCheck className="w-5 h-5 text-white" />
             </div>
             <div>
               <h2 className="text-white font-bold tracking-tight text-lg leading-tight">{t('surveys.portal.title').split('(')[0]}</h2>
               <p className="text-[10px] text-indigo-300 font-mono tracking-widest uppercase opacity-80">Lashing Engine</p>
             </div>
          </div>

          {/* Navigation Menu */}
          <nav className="p-4 space-y-1 mt-4">
            <button
              onClick={() => setActiveTab('pending')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${activeTab === 'pending' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800 hover:text-white'}`}
            >
              <div className="flex items-center gap-3">
                <Inbox className="w-4 h-4" />
                <span className="font-medium text-sm">{t('surveys.portal.dashboard.pending_tab')}</span>
              </div>
              {pendingAssignments.length > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${activeTab === 'pending' ? 'bg-indigo-800 text-indigo-100' : 'bg-slate-700 text-slate-300'}`}>
                  {pendingAssignments.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${activeTab === 'active' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800 hover:text-white'}`}
            >
              <div className="flex items-center gap-3">
                <ListTodo className="w-4 h-4" />
                <span className="font-medium text-sm">{t('surveys.portal.dashboard.active_tab')}</span>
              </div>
              {activeAssignments.length > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${activeTab === 'active' ? 'bg-indigo-800 text-indigo-100' : 'bg-slate-700 text-slate-300'}`}>
                  {activeAssignments.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${activeTab === 'completed' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800 hover:text-white'}`}
            >
              <div className="flex items-center gap-3">
                <CheckSquare className="w-4 h-4" />
                <span className="font-medium text-sm">{t('surveys.portal.dashboard.completed_tab')}</span>
              </div>
            </button>
          </nav>
        </div>

        {/* User / Setting / Logout area */}
        <div className="p-4 border-t border-white/10">
           <div className="flex items-center justify-between px-2 py-3 mb-2 rounded-lg bg-slate-800/50">
             <div className="flex items-center gap-3 max-w-full truncate">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center border border-slate-600 shrink-0">
                  <Activity className="w-4 h-4 text-slate-300" />
                </div>
                <div className="truncate">
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{t('surveys.portal.dashboard.connected')}</p>
                  <p className="text-sm text-white font-medium truncate">{t('surveys.portal.dashboard.account_type')}</p>
                </div>
             </div>
           </div>
           
           <button 
            onClick={() => {
              logout();
              window.location.href = '/surveyor/login';
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">{t('surveys.portal.dashboard.logout')}</span>
          </button>
        </div>
      </aside>

      {/* 2. MAIN CONTENT AREA */}
      <main className="flex-1 overflow-auto flex flex-col h-full bg-[#F9FAFB]">
        {/* Top Header Placeholder (Mobile Menu & Breadcrumb) */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-8 shrink-0 justify-between md:justify-start">
           <h2 className="text-lg font-semibold text-slate-800">{getTabTitle()}</h2>
           
           {/* Mobile Only Quick Navigation */}
           <div className="md:hidden flex gap-2">
             <button onClick={() => setActiveTab('pending')} className={`p-2 rounded ${activeTab === 'pending' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500'}`}><Inbox className="w-5 h-5"/></button>
             <button onClick={() => setActiveTab('active')} className={`p-2 rounded ${activeTab === 'active' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500'}`}><ListTodo className="w-5 h-5"/></button>
             <button onClick={() => setActiveTab('completed')} className={`p-2 rounded ${activeTab === 'completed' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500'}`}><CheckSquare className="w-5 h-5"/></button>
             <button onClick={() => { logout(); window.location.href = '/surveyor/login'; }} className="p-2 text-red-500"><LogOut className="w-5 h-5"/></button>
           </div>
        </header>

        {/* Content Body */}
        <div className="p-4 sm:p-8 flex-1">
          <div className="max-w-6xl mx-auto">
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-100 flex items-center gap-3">
                <XCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            {/* List View styled like Qdrant Clusters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
               {getActiveList().length === 0 ? (
                 <div className="flex flex-col items-center justify-center p-16 text-center">
                    <FileText className="w-16 h-16 text-slate-200 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-1">{t('surveys.portal.dashboard.no_records')}</h3>
                    <p className="text-slate-500 text-sm max-w-sm">{t('surveys.portal.dashboard.no_records_desc')}</p>
                 </div>
               ) : (
                 <div className="divide-y divide-slate-100">
                    {getActiveList().map(assignment => (
                      <div key={assignment.id} className="p-6 flex flex-col xl:flex-row xl:items-center justify-between gap-6 hover:bg-slate-50/50 transition-colors">
                        
                        {/* Task Specs */}
                        <div className="flex items-start gap-4 xl:w-5/12">
                          <div className="mt-1 p-2 bg-slate-100 rounded border border-slate-200 text-slate-500 shrink-0">
                            {assignment.status === 'pending' ? <Inbox className="w-5 h-5 text-amber-500" /> : 
                             assignment.status === 'accepted' ? <Activity className="w-5 h-5 text-blue-500" /> : 
                             <CheckCircle className="w-5 h-5 text-emerald-500" />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                               <h3 className="text-sm font-semibold text-slate-900 tracking-tight truncate" title={assignment.report_history?.project_description || (language === 'tr' ? 'İsimsiz Proje' : 'Untitled Project')}>
                                 {assignment.report_history?.project_description || (language === 'tr' ? 'İsimsiz Proje' : 'Untitled Project')}
                               </h3>
                               <p className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-500 border border-slate-200 shrink-0">
                                 {assignment.report_id}
                               </p>
                            </div>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-1">
                              <span className="font-semibold text-slate-800">{t('surveys.portal.dashboard.customer')}:</span> {assignment.customer_company || (language === 'tr' ? 'Bilinmiyor' : 'Unknown')}
                            </p>
                            <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5 font-medium">
                              <Clock className="w-3.5 h-3.5" /> {t('surveys.portal.dashboard.task_date')}: {new Date(assignment.assigned_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                            </div>
                          </div>
                        </div>

                        {/* Status Tags */}
                        <div className="flex flex-wrap items-center gap-3 xl:w-3/12 xl:justify-center">
                          {assignment.status === 'pending' && (
                             <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                               <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span> {t('surveys.portal.dashboard.waiting_response')}
                             </span>
                          )}
                          {assignment.status === 'accepted' && (
                             <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                               <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span> {t('surveys.portal.dashboard.under_review')}
                             </span>
                          )}
                          {assignment.status === 'completed' && (
                             <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                               <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {t('surveys.portal.dashboard.delivered')}
                             </span>
                          )}
                          {assignment.status === 'rejected' && (
                             <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                               <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> {t('surveys.portal.dashboard.rejected_by_you')}
                             </span>
                          )}
                        </div>

                        {/* Actions Array */}
                        <div className="flex items-center xl:justify-end gap-3 xl:w-4/12 mt-2 xl:mt-0">
                          {assignment.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateStatus(assignment.id, 'rejected')}
                                disabled={updatingId === assignment.id}
                                className="px-4 py-2 border border-slate-300 text-slate-700 font-medium text-sm rounded-md shadow-sm hover:bg-slate-50 transition-colors bg-white disabled:opacity-50"
                              >
                                {t('surveys.portal.dashboard.reject_button')}
                              </button>
                              <button
                                onClick={() => updateStatus(assignment.id, 'accepted')}
                                disabled={updatingId === assignment.id}
                                className="px-4 py-2 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-md shadow-sm transition-colors disabled:opacity-50"
                              >
                                {updatingId === assignment.id && <Loader2 className="w-4 h-4 animate-spin" />}
                                {t('surveys.portal.dashboard.accept_button')}
                              </button>
                            </>
                          )}

                          {assignment.status === 'accepted' && (
                            <button
                              onClick={() => navigate(`/surveyor/assignment/${assignment.id}`)}
                              className="w-full xl:w-auto px-6 py-2.5 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-lg shadow-sm transition-transform hover:scale-[1.02]"
                            >
                              {t('surveys.portal.dashboard.go_to_report')}
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          )}

                          {assignment.status === 'completed' && assignment.pdf_url && (
                             <button
                               onClick={() => handleDownloadPdf(assignment)}
                               className="w-full xl:w-auto px-5 py-2.5 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm rounded-lg shadow-sm transition-colors"
                             >
                                <Download className="w-4 h-4" /> 
                                {t('surveys.portal.dashboard.download_pdf')}
                             </button>
                          )}
                        </div>

                      </div>
                    ))}
                 </div>
               )}
            </div>

          </div>
        </div>
      </main>
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};
